/**
 * /preview route (Lane Q6) - renders an EigenApp bundle posted by a parent
 * page via APP_PREVIEW, WITHOUT PocketBase and WITHOUT a session.
 * Contract: eigenify-ai/docs/EIGENAPP-PREVIEW-PROTOCOL.md
 *
 * Handshake:
 *   1. iframe -> parent: EIGEN_PREVIEW_READY { protocolVersion: 1 } once the
 *      route is interactive (posted to every allowlisted origin - the
 *      allowlist is the Q7 fail-closed list, never "*").
 *   2. parent -> iframe: APP_PREVIEW { previewId, appJson, bundleText |
 *      bundleUrl, seedData? } (validated, caps enforced).
 *   3. iframe -> parent: APP_PREVIEW_RESULT { status: "rendered" | "error" }.
 *   A second APP_PREVIEW (same or new previewId) re-renders (hot reload).
 *
 * SECURITY (contract section 4): no session is ever read or held here -
 * REFRESH_RESULT/AUTH/LOGOUT are refused while on /preview (see
 * previewGuard.ts + PostMessageService), core.fromToken is never called, and
 * all data drivers are Local.
 */
import { FC, useEffect, useRef } from "react";
import { Navigate, Outlet, useNavigate, useParams } from "react-router";
import { Layout } from "antd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePostMessage } from "@/hooks/usePostMessage";
import { POSTMESSAGE_ALLOWED_ORIGINS } from "@/config/constants";
import {
  createAppPreviewHandler,
  PREVIEW_PROTOCOL_VERSION,
  ValidatedAppPreview,
  SendPreviewResult,
} from "@/services/postMessage/handlers/AppPreviewHandler";
import { PreviewReadyMessage } from "@/services/postMessage/types";
import {
  executePreviewBundle,
  fetchPreviewBundle,
  registerPreviewApp,
  teardownPreviewApp,
} from "@/preview/previewRuntime";
import { previewStore, useActivePreview } from "@/preview/previewStore";
import { AppRenderer } from "../app";

const { Content } = Layout;

const PREVIEW_BASE = "/preview/app";

/**
 * Announce readiness to the parent. The parent's origin cannot be read
 * cross-origin before its first message, so READY is posted to every
 * allowlisted origin (only the real parent receives its copy). With the Q7
 * fail-closed empty allowlist there is nowhere safe to announce to.
 */
function postPreviewReady(): void {
  if (window.parent === window) return;

  if (POSTMESSAGE_ALLOWED_ORIGINS.length === 0) {
    console.error(
      "[PreviewScreen] Cannot announce EIGEN_PREVIEW_READY: the postMessage origin allowlist is " +
        "empty (fail closed). Set VITE_POSTMESSAGE_ALLOWED_ORIGINS."
    );
    return;
  }

  const ready: PreviewReadyMessage = {
    type: "EIGEN_PREVIEW_READY",
    protocolVersion: PREVIEW_PROTOCOL_VERSION,
  };

  for (const origin of POSTMESSAGE_ALLOWED_ORIGINS) {
    if (origin.includes("*")) continue; // patterns cannot be postMessage targets
    try {
      window.parent.postMessage(ready, origin);
    } catch (error) {
      console.warn(`[PreviewScreen] Failed to post READY to ${origin}:`, error);
    }
  }
}

/**
 * Layout route for /preview: registers the APP_PREVIEW handler (it stays
 * mounted across child navigations, so hot reload keeps working while the
 * app is being viewed) and posts READY once.
 */
export const PreviewShell: FC = () => {
  const navigate = useNavigate();
  const service = usePostMessage();
  // Serialize renders: a second APP_PREVIEW during a render queues behind it.
  const renderChain = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    if (!service) return;

    const renderPreview = async (
      payload: ValidatedAppPreview,
      sendResult: SendPreviewResult
    ): Promise<void> => {
      try {
        const bundleText = payload.bundleText ?? (await fetchPreviewBundle(payload.bundleUrl!));
        const app = await executePreviewBundle(bundleText);

        // Hot reload: tear down the previous mount before registering anew.
        const previous = previewStore.get();
        if (previous) await teardownPreviewApp(previous.appSpecifier);

        const seq = previewStore.nextSeq();
        const { appSpecifier, initialViewId } = await registerPreviewApp(
          app,
          seq,
          payload.seedData
        );

        previewStore.set({ previewId: payload.previewId, seq, app, appSpecifier });

        const viewSegment = initialViewId ? `/${initialViewId}` : "";
        navigate(
          `${PREVIEW_BASE}/${appSpecifier.appId}/${appSpecifier.deploymentId}${viewSegment}`,
          { replace: true }
        );

        sendResult({ previewId: payload.previewId, status: "rendered" });
      } catch (error) {
        // Surface the failure in the waiting screen too.
        previewStore.set(null);
        navigate("/preview", { replace: true });
        throw error; // createAppPreviewHandler sends the sanitized error result
      }
    };

    const handler = createAppPreviewHandler({
      onPreview: (payload, sendResult) => {
        const next = renderChain.current.then(() => renderPreview(payload, sendResult));
        // Keep the chain alive after failures; the handler reported them.
        renderChain.current = next.catch(() => undefined);
        return next;
      },
    });

    const unsubscribe = service.subscribe("APP_PREVIEW", handler);
    postPreviewReady();
    return () => unsubscribe();
  }, [service, navigate]);

  return <Outlet />;
};

/** Index child of /preview: shown until the first APP_PREVIEW arrives. */
export const PreviewWaitingScreen: FC = () => {
  return (
    <Layout className="h-screen flex items-center justify-center bg-background">
      <Content>
        <Card className="max-w-md mx-auto">
          <CardHeader>
            <CardTitle>App preview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-center">
              Waiting for an app to preview...
            </p>
          </CardContent>
        </Card>
      </Content>
    </Layout>
  );
};

/**
 * /preview/app/:appId/:deploymentId(/:viewId) - renders the posted App with
 * the SAME AppRenderer the experimental loader route uses (no second
 * renderer). No router loader: the App comes from the preview store, which
 * only APP_PREVIEW populates.
 */
export const PreviewAppScreen: FC = () => {
  const preview = useActivePreview();
  const { appId, deploymentId } = useParams();

  if (
    !preview ||
    preview.appSpecifier.appId !== appId ||
    preview.appSpecifier.deploymentId !== deploymentId
  ) {
    // Deep link without a posted preview (or a stale render path).
    return <Navigate to="/preview" replace />;
  }

  // key={seq} forces a clean remount per render (hot reload).
  return <AppRenderer key={preview.seq} app={preview.app} base={PREVIEW_BASE} />;
};
